import {
  Button,
  Center,
  ConditionalRendering,
  Loader,
  Text
} from "@marketplace/ui";
import { getRouteApi } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { HiPlus } from "react-icons/hi";

import { NoSellerIllustraction } from "@/assets/images/no-seller-illustration";
import { Pagination } from "@/components";

import { AddSellerTypeModal } from "../../components/add-seller-type-modal";
import { DeleteSellerModal } from "../../components/delete-seller-modal";
import { ResendVerificationModal } from "../../components/resend-verification-modal";
import { SellerListFilters } from "../../components/seller-list-filters";
import { SellerListTable } from "../../components/seller-list-table";
import { useGetSellerListQuery } from "../../hooks/queries/use-get-seller-list-query";

const route = getRouteApi("/users/sellers/");

const NoSeller = ({ onOpen }: { onOpen: () => void }) => {
  const { t } = useTranslation("users");

  return (
    <div className="flex flex-col justify-center items-center gap-y-6 mt-[56px]">
      <NoSellerIllustraction />
      <div className="text-center">
        <Text type="heading" size="xl" className="mb-2 text-fjord-grey-500">
          {t("sellerListPage.emptySeller.title")}
        </Text>
        <Text type="paragraph" size="l" className="text-fjord-grey-300">
          {t("sellerListPage.emptySeller.description")}
        </Text>
      </div>
      <Button
        variant="primary"
        rightSection={<HiPlus size={16} />}
        onClick={onOpen}
      >
        {t("sellerListPage.addSeller")}
      </Button>
    </div>
  );
};

export const SellerListPage = () => {
  const { page, q, sort } = route.useSearch();
  const navigate = route.useNavigate();
  const { t } = useTranslation("users");
  const [sellerTypeModalOpened, setSellerTypeModalOpened] = useState(false);

  const { data, isLoading } = useGetSellerListQuery({
    queryParams: {
      page,
      limit: 10,
      q,
      sort
    }
  });

  const sellerListData = data?.data;
  const metadata = data?.metadata;

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div>
          <Text type="heading" size="l" className="text-humble-blue-500">
            {t("sellerListPage.title")}
          </Text>
          <Text className="text-fjord-grey-400">
            {t("sellerListPage.subTitle")}
          </Text>
        </div>
        <Button
          variant="primary"
          rightSection={<HiPlus size={16} />}
          onClick={() => setSellerTypeModalOpened(true)}
        >
          {t("sellerListPage.addSeller")}
        </Button>
      </div>

      <SellerListFilters />

      <ConditionalRendering
        condition={sellerListData && sellerListData!.length > 0}
      >
        <div className="space-y-6">
          <SellerListTable records={sellerListData ?? []} />

          <Pagination
            currentPageTotal={sellerListData?.length}
            totalData={metadata?.total}
            total={metadata?.totalPage ?? 0}
            value={metadata?.page}
            onChange={(value) =>
              navigate({
                search: {
                  page: value
                }
              })
            }
          />
        </div>
      </ConditionalRendering>

      <ConditionalRendering condition={isLoading}>
        <Center className="my-20">
          <Loader />
        </Center>
      </ConditionalRendering>

      <ConditionalRendering
        condition={sellerListData && sellerListData?.length === 0}
      >
        <NoSeller onOpen={() => setSellerTypeModalOpened(true)} />
      </ConditionalRendering>

      <DeleteSellerModal />
      <ResendVerificationModal />
      <AddSellerTypeModal
        opened={sellerTypeModalOpened}
        onClose={() => setSellerTypeModalOpened(false)}
      />
    </>
  );
};
